import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs, updateDoc, doc, writeBatch } from "firebase/firestore";
import { db } from "../firebase/config";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "./ui/table";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, GripVertical, Plus, FolderOpen, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

const typesZone = [
  { value: "prairie", label: "Prairie" },
  { value: "paddock", label: "Paddock" },
  { value: "pature", label: "Pâture" },
  { value: "batiment", label: "Bâtiment" },
  { value: "box", label: "Box" },
  { value: "groupe", label: "Groupe" },
];

function CouleurPastille({ color }) {
  return (
    <span
      className="inline-block w-6 h-6 rounded-full border"
      style={{ background: color || "#e5e5e5" }}
      title={color}
    />
  );
}

export default function ZoneList() {
  const [zones, setZones] = useState([]);
  const [occupationMap, setOccupationMap] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    type: "",
    color: "",
    isOccupable: true,
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragCounter = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchZones();
  }, []);

  async function fetchZones() {
    const [lieuxSnap, animauxSnap] = await Promise.all([
      getDocs(collection(db, "lieux")),
      getDocs(collection(db, "animaux")),
    ]);
    
    let lieux = lieuxSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      order: doc.data().order ?? 999,
      level: doc.data().level ?? 0,
      parentId: doc.data().parentId ?? null,
      isOccupable: doc.data().isOccupable ?? true,
      isVirtual: doc.data().isVirtual ?? false,
    }));
    
    // Trier par ordre
    lieux.sort((a, b) => a.order - b.order);
    
    // Réindexer les ordres pour s'assurer qu'ils sont consécutifs
    lieux = lieux.map((zone, index) => ({
      ...zone,
      order: index
    }));
    
    setZones(lieux);
    
    const counts = {};
    for (const d of animauxSnap.docs) {
      const z = d.data().zoneId;
      if (z) counts[z] = (counts[z] || 0) + 1;
    }
    setOccupationMap(counts);
  }

  function handleEdit(zone) {
    setEditing(zone.id);
    setForm({
      name: zone.name || "",
      type: zone.type || "",
      color: zone.color || "",
      isOccupable: zone.isOccupable ?? true,
      isVirtual: zone.isVirtual ?? false,
    });
  }

  function handleCancel() {
    setEditing(null);
    setForm({ name: "", type: "", color: "", isOccupable: true });
  }

  async function handleSave() {
    if (!form.name || !form.type) {
      alert("Nom et type obligatoires !");
      return;
    }
    await updateDoc(doc(db, "lieux", editing), {
      name: form.name,
      type: form.type,
      color: form.color,
      isOccupable: form.isVirtual ? false : form.isOccupable,
      isVirtual: form.isVirtual,
    });
    setEditing(null);
    fetchZones();
  }

  async function handleCreateVirtual() {
    const name = prompt("Nom de la zone virtuelle (conteneur) :");
    if (!name) return;
    
    const type = prompt("Type de regroupement (ex: village, secteur, région):", "groupe");
    if (!type) return;
    
    try {
      const batch = writeBatch(db);
      
      // Créer la nouvelle zone virtuelle
      const newZoneRef = doc(collection(db, "lieux"));
      batch.set(newZoneRef, {
        name: name,
        type: type || "groupe",
        color: "#cccccc",
        isOccupable: false,
        isVirtual: true,  // Nouveau champ pour les zones virtuelles
        coordinates: [],  // Pas de coordonnées pour les zones virtuelles
        level: 0,
        parentId: null,
        order: zones.length,
        capacity: 0
      });
      
      await batch.commit();
      fetchZones();
      alert(`Zone virtuelle "${name}" créée avec succès`);
    } catch (error) {
      console.error("Erreur lors de la création:", error);
      alert("Erreur lors de la création de la zone virtuelle");
    }
  }

  // Fonction pour naviguer vers la carte avec la zone sélectionnée
  function handleGoToMap(zoneId) {
    // Navigation vers la carte avec le zoneId en paramètre
    navigate(`/carte?zone=${zoneId}`);
  }

  // Fonctions de drag & drop
  function handleDragStart(e, index) {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragEnter(e, index) {
    e.preventDefault();
    dragCounter.current++;
    if (draggedItem !== null && draggedItem !== index) {
      setDragOverIndex(index);
    }
  }

  function handleDragLeave(e) {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverIndex(null);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }

  async function handleDrop(e, dropIndex) {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOverIndex(null);
    
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      return;
    }

    const newZones = [...zones];
    const draggedZone = newZones[draggedItem];
    
    // Retirer l'élément de sa position actuelle
    newZones.splice(draggedItem, 1);
    
    // Calculer le nouvel index après suppression
    const newDropIndex = draggedItem < dropIndex ? dropIndex - 1 : dropIndex;
    
    // Insérer à la nouvelle position
    newZones.splice(newDropIndex, 0, draggedZone);
    
    // Réindexer les ordres
    const reorderedZones = newZones.map((zone, index) => ({
      ...zone,
      order: index
    }));
    
    // Ajuster le niveau et le parent du zone déplacé
    if (newDropIndex > 0) {
      const prevZone = reorderedZones[newDropIndex - 1];
      // Par défaut, prendre le même niveau que le précédent
      reorderedZones[newDropIndex].level = prevZone.level;
      // Recalculer le parent basé sur le nouveau placement
      reorderedZones[newDropIndex].parentId = findParentId(reorderedZones, newDropIndex);
    } else {
      // Premier élément = racine
      reorderedZones[newDropIndex].level = 0;
      reorderedZones[newDropIndex].parentId = null;
    }
    
    setZones(reorderedZones);
    setDraggedItem(null);
    
    // Sauvegarder dans Firebase
    await saveZonesOrder(reorderedZones);
  }

  function handleDragEnd() {
    setDraggedItem(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  }

  // Fonctions d'indentation
  async function handleIndentRight(index) {
    if (index === 0) return; // Pas de parent possible pour le premier élément
    
    const newZones = [...zones];
    const currentZone = newZones[index];
    const prevZone = newZones[index - 1];
    
    // Ne pas indenter plus que le niveau du précédent + 1
    if (currentZone.level <= prevZone.level) {
      currentZone.level = currentZone.level + 1;
      currentZone.parentId = findParentId(newZones, index);
      
      setZones(newZones);
      await saveZonesOrder(newZones);
    }
  }

  async function handleIndentLeft(index) {
    const newZones = [...zones];
    const currentZone = newZones[index];
    
    if (currentZone.level > 0) {
      const oldLevel = currentZone.level;
      currentZone.level = currentZone.level - 1;
      currentZone.parentId = findParentId(newZones, index);
      
      // Ajuster les enfants qui suivent
      for (let i = index + 1; i < newZones.length; i++) {
        if (newZones[i].level > oldLevel) {
          newZones[i].level = newZones[i].level - 1;
          newZones[i].parentId = findParentId(newZones, i);
        } else {
          break; // On s'arrête au premier élément de même niveau ou inférieur
        }
      }
      
      setZones(newZones);
      await saveZonesOrder(newZones);
    }
  }

  // Trouver le parent basé sur la position et le niveau
  function findParentId(zonesList, index) {
    const currentZone = zonesList[index];
    if (currentZone.level === 0) return null;
    
    // Chercher en arrière le premier élément avec un niveau inférieur
    for (let i = index - 1; i >= 0; i--) {
      if (zonesList[i].level < currentZone.level) {
        return zonesList[i].id;
      }
    }
    return null;
  }

  // Sauvegarder l'ordre dans Firebase
  async function saveZonesOrder(orderedZones) {
    const batch = writeBatch(db);
    
    orderedZones.forEach(zone => {
      const zoneRef = doc(db, "lieux", zone.id);
      batch.update(zoneRef, {
        order: zone.order,
        level: zone.level,
        parentId: zone.parentId
      });
    });
    
    await batch.commit();
  }

  return (
    <div className="w-full mt-4">
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold">Gestion des zones</h2>
        <Button
          onClick={handleCreateVirtual}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Créer zone virtuelle
        </Button>
      </div>
      <Table className="w-full table-auto md:table-fixed">
        <colgroup>
          <col style={{ width: "30%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "15%" }} />
        </colgroup>

        <TableHeader>
          <TableRow>
            <TableHead className="p-2 text-left">Nom</TableHead>
            <TableHead className="p-2 text-left">Type</TableHead>
            <TableHead className="p-2 text-left">Occupable</TableHead>
            <TableHead className="p-2 text-left">Occupation</TableHead>
            <TableHead className="p-2 text-left">Couleur</TableHead>
            <TableHead className="p-2 text-center">Localisation</TableHead>
            <TableHead className="p-2 text-left">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {zones.map((zone, index) => (
            <TableRow 
              key={zone.id} 
              className={`border-t transition-all ${
                dragOverIndex === index ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''
              } ${draggedItem === index ? 'opacity-50' : ''}`}
              draggable={editing !== zone.id}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              {editing === zone.id ? (
                <>
                  <TableCell className="p-2">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400 cursor-move opacity-0" />
                      <div style={{ marginLeft: `${zone.level * 24}px` }} className="flex-1">
                        <input
                          className="border px-2 py-1 rounded w-full"
                          value={form.name}
                          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="p-2">
                    <select
                      className="border px-2 py-1 rounded w-full"
                      value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                      required
                    >
                      <option value="">Type...</option>
                      {typesZone.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </TableCell>
                  
                  <TableCell className="p-2">
                    {form.isVirtual ? (
                      <span className="text-xs text-gray-400 italic">Zone virtuelle</span>
                    ) : (
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.isOccupable}
                          onChange={e => setForm(f => ({ ...f, isOccupable: e.target.checked }))}
                        />
                        <span className="text-sm">Occupable</span>
                      </label>
                    )}
                  </TableCell>

                  <TableCell className="p-2 text-center">
                    {form.isOccupable ? (occupationMap[zone.id] || 0) : '-'}
                  </TableCell>
                  <TableCell className="p-2">
                    <input
                      className="border px-2 py-1 rounded w-full"
                      type="color"
                      value={form.color || "#e5e5e5"}
                      onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                    />
                  </TableCell>
                  <TableCell className="p-2 text-center">
                    {/* Colonne vide en mode édition */}
                    -
                  </TableCell>
                  <TableCell className="p-2">
                    <div className="flex gap-1">
                      <button 
                        className="px-2 py-1 rounded bg-black text-white hover:bg-neutral-900 text-sm" 
                        onClick={handleSave}
                      >
                        Enregistrer
                      </button>
                      <button 
                        className="px-2 py-1 rounded bg-white border border-neutral-300 text-neutral-900 hover:bg-neutral-50 text-sm" 
                        onClick={handleCancel}
                      >
                        Annuler
                      </button>
                    </div>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell className="p-2 text-left font-medium">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                      <div style={{ marginLeft: `${zone.level * 24}px` }} className="flex items-center gap-2">
                        {zone.isVirtual && (
                          <FolderOpen className="w-4 h-4 text-gray-500" title="Zone virtuelle (conteneur)" />
                        )}
                        <span className={zone.isVirtual ? "italic text-gray-600" : ""}>
                          {zone.name || "-"}
                        </span>
                        {zone.parentId && (
                          <span className="text-xs text-gray-500">
                            (sous {zones.find(z => z.id === zone.parentId)?.name})
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  
                  <TableCell className="p-2 text-left">
                    {zone.isVirtual ? (
                      <span className="text-gray-500 italic">Virtuelle ({zone.type})</span>
                    ) : (
                      typesZone.find(t => t.value === zone.type)?.label || zone.type
                    )}
                  </TableCell>

                  <TableCell className="p-2 text-left">
                    <span className={`text-sm ${zone.isOccupable ? 'text-green-600' : 'text-gray-400'}`}>
                      {zone.isOccupable ? '✓ Oui' : 'Non'}
                    </span>
                  </TableCell>
                  
                  <TableCell className="p-2 text-center">
                    {zone.isVirtual ? (
                      <span className="text-xs text-gray-400">N/A</span>
                    ) : zone.isOccupable ? (
                      occupationMap[zone.id] || 0
                    ) : '-'}
                  </TableCell>
                  
                  <TableCell className="p-2 text-left">
                    <CouleurPastille color={zone.color} />
                  </TableCell>
                  
                  <TableCell className="p-2 text-center">
                    {!zone.isVirtual && (
                      <button
                        onClick={() => handleGoToMap(zone.id)}
                        className="p-2 rounded hover:bg-gray-100 transition-colors"
                        title={`Localiser ${zone.name} sur la carte`}
                      >
                        <MapPin className="w-5 h-5 text-black" />
                      </button>
                    )}
                  </TableCell>
                  
                  <TableCell className="p-2">
                    <div className="flex gap-1 items-center">
                      <button
                        onClick={() => handleIndentLeft(index)}
                        disabled={zone.level === 0}
                        className={`p-1 rounded ${
                          zone.level === 0 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Réduire l'indentation"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleIndentRight(index)}
                        disabled={index === 0 || zone.level > zones[index - 1].level}
                        className={`p-1 rounded ${
                          index === 0 || zone.level > zones[index - 1].level
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Augmenter l'indentation"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <Button 
                        onClick={() => handleEdit(zone)} 
                        variant="default"
                        size="sm"
                        className="ml-2"
                      >
                        Éditer
                      </Button>
                    </div>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {zones.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          Aucune zone configurée
        </div>
      )}
    </div>
  );
}